import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { getPayPeriodList } from "requests/homeowner/fieldReps";
import { MSpinner, MCard, MGrid, MDialog, ACTION_CLOSE } from "components/Shared";
import { ACTION_DOWNLOAD } from "components/Shared/MGrid";
import Button from "@mui/material/Button";
import { reviewPayPeriod, calculatePayPeriod, getExecutions, exportPayPeriod, closePayPeriod } from "requests/payroll";
import MSectionCollection, { MSECTION_MODE_TAB } from "components/Shared/MSectionCollection";
import { DATA_TYPE_NUMBER, DATA_TYPE_NUMBER_HUNDREDTHS, DATA_TYPE_NUMBER_UNFORMATTED, DATA_TYPE_BOOLEAN, DATA_TYPE_MONEY, DATA_TYPE_DATE, DATA_TYPE_DATE_TIME } from "typeConstants/dataTypes";
import xlsx from "xlsx";
import { userHasRight } from "selectors/rights";
import { PAYROLL_RUN } from "typeConstants/rights";
import { formatWorksheetColumns } from "components/Shared/Functions";
import { dateTimeToString } from "components/Shared/Formatters";
import { PayrollReviewDto, PayrollExecutionDto } from "interfaces/Mueller.Business.Models";
import MSelect, { MSelectOption } from "components/Shared/MSelect.tsx";

const columnDefaults = c => ({ ...c, key: c.name });

const appraiserPayrollGridDefs = [
  { name: "Appraiser #", dataSource: "appraiser.number", dataType: DATA_TYPE_NUMBER_UNFORMATTED, width: 95 },
  { name: "First Name", dataSource: "appraiser.firstName", width: 130 },
  { name: "Last Name", dataSource: "appraiser.lastName", width: 130  },
  { name: "Supervisor", dataSource: "supervisor.firstLast", width: 150 },
  { name: "Is Approved", dataSource: "isApproved", dataType: DATA_TYPE_BOOLEAN, width: 93 },
  { name: "Case Count", dataSource: "casesCount", dataType: DATA_TYPE_NUMBER, width: 90 },
  { name: "Hourly Rate", dataSource: "hourlyRateAgreed", dataType: DATA_TYPE_MONEY, width: 92 },
  { name: "Hours Prod.", dataSource: "hoursWorked", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 91 },
  { name: "Cost Current", dataSource: "costCurrent", dataType: DATA_TYPE_MONEY, width: 99 },
  { name: "Cost Previous", dataSource: "costPrevious", dataType: DATA_TYPE_MONEY, width: 107 },
  { name: "Cost Total", dataSource: "costTotal", dataType: DATA_TYPE_MONEY, width: 80 },
  { name: "Late Deduction", dataSource: "lateDeduction", dataType: DATA_TYPE_MONEY, width: 112 },
  { name: "Returned Deduction", dataSource: "returnedDeduction", dataType: DATA_TYPE_MONEY, width: 142 },
  { name: "MAC Pay", dataSource: "macPay", dataType: DATA_TYPE_MONEY, width: 80 },
  { name: "MAC Bonus", dataSource: "macBonusPay", dataType: DATA_TYPE_MONEY, width: 91 },
  { name: "Effective Hourly Rate", dataSource: "effectiveHourlyRate", dataType: DATA_TYPE_MONEY, width: 155 },
  { name: "Cost Per Case", dataSource: "costPerCase", dataType: DATA_TYPE_MONEY, width: 110 },
].map(columnDefaults);

const inspectorPayrollGridDefs = [
  { name: "Inspector #", dataSource: "inspector.number", dataType: DATA_TYPE_NUMBER_UNFORMATTED, width: 91 },
  { name: "First Name", dataSource: "inspector.firstName", width: 130 },
  { name: "Last Name", dataSource: "inspector.lastName", width: 130 },
  { name: "Supervisor", dataSource: "supervisorName", width: 150 },
  { name: "Is Approved", dataSource: "isApproved", dataType: DATA_TYPE_BOOLEAN, width: 93 },
  { name: "State", dataSource: "state", width: 80 },
  { name: "Payroll ID", dataSource: "payrollEmployeeID", width: 80 },
  { name: "Hourly Rate", dataSource: "hourlyPayRate", width: 92, dataType: DATA_TYPE_MONEY },
  { name: "Is Team Lead", dataSource: "isTeamLeader", dataType: DATA_TYPE_BOOLEAN, width: 94 },
  { name: "TL Pay Rate", dataSource: "teamLeaderPayRate", width: 93, dataType: DATA_TYPE_MONEY },
  { name: "Prod Bonus", dataSource: "productionBonus", dataType: DATA_TYPE_MONEY, width: 91 },
  { name: "Prod Hours", dataSource: "productionHours", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 89 },
  { name: "Previous Hours", dataSource: "previousHours", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 120 },
  { name: "Admin Hours", dataSource: "adminHours", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 101 },
  { name: "Admin TL Hours", dataSource: "adminTeamLeadHours", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 118 },
  { name: "Total Hours", dataSource: "totalHours", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 90 },
  { name: "Prod Mileage", dataSource: "productionMileage", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 101 },
  { name: "Admin Mileage", dataSource: "adminMileage", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 113 },
  { name: "Total Mileage", dataSource: "totalMileage", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 103 },
  { name: "Total Mileage Reimbursement", dataSource: "totalMileageReimbursement", dataType: DATA_TYPE_MONEY, width: 205 },
  { name: "Effective Hourly Rate", dataSource: "effectiveHourlyRate", dataType: DATA_TYPE_MONEY, width: 155 },
].map(columnDefaults);

const inspectorContractorPayrollGridDefs = [
  { name: "Inspector #", dataSource: "inspector.number", dataType: DATA_TYPE_NUMBER_UNFORMATTED, width: 91 },
  { name: "First Name", dataSource: "inspector.firstName", width: 130 },
  { name: "Last Name", dataSource: "inspector.lastName", width: 130 },
  { name: "Supervisor", dataSource: "supervisor.firstLast", width: 150 },
  { name: "Is Approved", dataSource: "isApproved", dataType: DATA_TYPE_BOOLEAN, width: 93 },
  { name: "State", dataSource: "state", width: 80 },
  { name: "Mac", dataSource: "mac", dataType: DATA_TYPE_MONEY, width: 91 },
].map(columnDefaults);

const inspectorCaseLogGridDefs = [
  { name: "Payroll ID", dataSource: "employee.number", dataType: DATA_TYPE_NUMBER_UNFORMATTED, width: 91 },
  { name: "First Name", dataSource: "employee.firstName", width: 130 },
  { name: "Last Name", dataSource: "employee.lastName", width: 130 },
  { name: "Case", dataSource: "caseLog.controlIdNumber", dataType: DATA_TYPE_NUMBER_UNFORMATTED, width: 80 },
  { name: "Inspected Date", dataSource: "inspectedDate", dataType: DATA_TYPE_DATE, width: 150 },
  { name: "Site Time", dataSource: "caseLog.fieldHours", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 95 },
  { name: "Web Time", dataSource: "caseLog.webHours", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 95 },
  { name: "Drive Time", dataSource: "caseLog.travelHours", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 95 },
  { name: "Total Time", dataSource: "caseLog.totalHours", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 95 },
].map(columnDefaults);

const qaPayrollGridDefs = [
  { name: "Payroll ID", dataSource: "qualityAssurance.number", dataType: DATA_TYPE_NUMBER_UNFORMATTED, width: 95 },
  { name: "First Name", dataSource: "qualityAssurance.firstName", width: 130 },
  { name: "Last Name", dataSource: "qualityAssurance.lastName", width: 130 },
  { name: "Role", dataSource: "employeeRole.name", width: 130 },
  { name: "Supervisor", dataSource: "supervisor.firstLast", width: 150 },
  { name: "Is Approved", dataSource: "isApproved", dataType: DATA_TYPE_BOOLEAN, width: 93 },
  { name: "Is Missing Clock Info", dataSource: "isMissingClockOut", dataType: DATA_TYPE_BOOLEAN, width: 150 },
  { name: "Case Count", dataSource: "casesCount", dataType: DATA_TYPE_NUMBER, width: 90 },
  { name: "Hourly Rate", dataSource: "hourlyRateAgreed", dataType: DATA_TYPE_MONEY, width: 95 },
  { name: "Hours Prod.", dataSource: "hoursProduction", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 95 },
  { name: "Hours Total", dataSource: "hoursTotal", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 95 },
  { name: "Week Bonus", dataSource: "macBonusPay", dataType: DATA_TYPE_MONEY, width: 95 },
  { name: "Bonus Rollup", dataSource: "macBonusPayRollup", dataType: DATA_TYPE_MONEY, width: 120 },
].map(columnDefaults);

const saGridDefs = [
  { name: "Payroll ID", dataSource: "user.number", dataType: DATA_TYPE_NUMBER_UNFORMATTED, width: 95 },
  { name: "First Name", dataSource: "user.firstName", width: 130 },
  { name: "Last Name", dataSource: "user.lastName", width: 130 },
  { name: "Supervisor", dataSource: "supervisor.firstLast", width: 150 },
  { name: "Is Approved", dataSource: "isApproved", dataType: DATA_TYPE_BOOLEAN, width: 93 },
  { name: "Is Missing Clock Info", dataSource: "isMissingClockInfo", dataType: DATA_TYPE_BOOLEAN, width: 150 },
  { name: "Case Count", dataSource: "casesCount", dataType: DATA_TYPE_NUMBER, width: 90 },
  { name: "Hourly Rate", dataSource: "hourlyRateAgreed", dataType: DATA_TYPE_MONEY, width: 95 },
  { name: "Hours Prod.", dataSource: "productionHours", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 95 },
  { name: "Hours Total", dataSource: "hoursWorked", dataType: DATA_TYPE_NUMBER_HUNDREDTHS, width: 95 },
].map(columnDefaults);

interface LocalPayPeriod extends MSelectOption {
  label: string;
  value: number;
  isClosed: boolean;
}

interface LocalDialog {
  title: string;
  width: string;
  children: React.ReactElement;
  contentActions?: React.ReactElement;
}

interface Props {
  canRunPayroll: boolean;
}

const Payroll = ({ canRunPayroll } : Props) => {
  const mounted = useRef(true);
  const [payPeriods, setPayPeriods] = useState<LocalPayPeriod[]>();
  const [selectedPayPeriod, setSelectedPayPeriod] = useState<LocalPayPeriod>();
  const [payrollSummary, setPayrollSummary] = useState<PayrollReviewDto>(null);
  const [isRunningExport, setIsRunningExport] = useState<boolean>(false);
  const [dialog, setDialog] = useState<LocalDialog>();

  const [lastExecution, setLastExecution] = useState<PayrollExecutionDto | null | undefined>(undefined);
  const [summaryText, setSummaryText] = useState<string>("");
  const [pollTimeout, setPollTimeout] = useState<number>(0);

  useEffect(() => {
    if (selectedPayPeriod) {
      window.clearTimeout(pollTimeout);
      setPayrollSummary(null);
      setLastExecution(undefined);
      getExecutions(selectedPayPeriod.value, onReceivedExecutions, onReceivedExecutionsError, mounted);
    }
  }, [selectedPayPeriod]);

  useEffect(() => {
    if(!selectedPayPeriod) return;

    if (lastExecution === undefined) { // loading
      setSummaryText("");
    }
    else if (lastExecution === null) { // not run
      setSummaryText("Not Yet Run");
    }
    else if (lastExecution.executeFinishDateTime) { // finished
      const prefix = lastExecution.hadError ? "Error" : "Finished Run";

      setSummaryText(`${prefix}: ${new Date(lastExecution.executeFinishDateTime).toLocaleString()} - ${lastExecution.internetUser.firstLast}`);
    }
    else { // still running
      const dateToUse = new Date(lastExecution.executeStartDateTime ?? lastExecution.createdDateTime);
      setSummaryText(`Started Run: ${dateToUse.toLocaleString()} - ${lastExecution.internetUser.firstLast}`);

      setPollTimeout(window.setTimeout(() => {
        getExecutions(selectedPayPeriod.value, onReceivedExecutions, onReceivedExecutionsError, mounted);
      }, 60000));
    }
  }, [lastExecution]);

  const getPayPeriods = () => getPayPeriodList(onPayPeriodsReceived, mounted);

  useEffect(() => {
    getPayPeriods();
    return () => {
      mounted.current = false;
      window.clearTimeout(pollTimeout);
    };
  }, []);

  const onPayPeriodsReceived = pp => {
    let mapped: LocalPayPeriod[] = pp.map(p => ({ label: p.fromToDisplay, value: p.id, isClosed: !!p.closedDate }));
    setPayPeriods(mapped);
    //when we close a pay period we update our pay period list so the Pending text goes away to tell us it was closed
    //we then need to update the selected period too
    if (selectedPayPeriod) setSelectedPayPeriod(mapped.find(m => m.value === selectedPayPeriod.value));
  };

  const onReceivedExecutions = (list: PayrollExecutionDto[]) => {
    const sorted = list.sort((a, b) => new Date(b.createdDateTime).getDate() - new Date(a.createdDateTime).getDate());
    setLastExecution(sorted.length > 0 ? sorted[0] : null);
    reviewPayPeriod(selectedPayPeriod.value, setPayrollSummary, mounted);
  };

  const onReceivedExecutionsError = () => {
    setTimeout(() => {
      getExecutions(selectedPayPeriod.value, onReceivedExecutions, onReceivedExecutionsError, mounted);
    },10000);
  };

  const onCalculateClicked = () => {
    setPayrollSummary(null);
    calculatePayPeriod(selectedPayPeriod.value, () => {
      getExecutions(selectedPayPeriod.value, onReceivedExecutions, onReceivedExecutionsError, mounted);
    }, mounted);
  };

  const onExportClicked = () => {
    setIsRunningExport(true);
    exportPayPeriod(selectedPayPeriod.value, onExportFinished, mounted);
  };

  const downloadCSVExport = ({ fileName, columnDefinitions, data }, showHeader) => {
    if (!data || data.length === 0) return;

    let headerRow = columnDefinitions.reduce((acc, d) => ({ ...acc, [d.dataSource]: d.name}), {});

    let options = {
      header: columnDefinitions.map(c => c.dataSource), //header to specify the order of the headers
      skipHeader: true, //sheetJS will make the header with the object keys which is not desired - so we will add our own when we want to
    };

    let sheetData = data;
    if (showHeader) sheetData = [headerRow, ...data];

    columnDefinitions.forEach(cd => {
      if (cd.dataType === DATA_TYPE_DATE_TIME) {
        data.forEach(d => d[cd.dataSource] = dateTimeToString(d[cd.dataSource]));
      }
    });

    const wb = xlsx.utils.book_new();
    const ws = xlsx.utils.json_to_sheet(sheetData, options);

    formatWorksheetColumns(columnDefinitions, ws);

    xlsx.utils.book_append_sheet(wb, ws, "Sheet");
    xlsx.writeFile(wb, fileName);
  };

  const onExportDownloadClicked = (data) => {
    downloadCSVExport(data.exportResult.contractorDollarCard, false);
    downloadCSVExport(data.exportResult.hourlyDollarCard, false);
    downloadCSVExport(data.exportResult.salariedDollarCard, false);
    downloadCSVExport(data.exportResult.timeCard, true);
    downloadCSVExport(data.inspectorPaymentCaseList, true);
    downloadCSVExport(data.appraiserPaymentCaseList, true);
    setDialog(null);
  };

  const onExportFinished = data => {
    setIsRunningExport(false);
    setPayrollSummary(data.reviewResult);

    let noData = [];
    if (data.exportResult.contractorDollarCard.data.length === 0) noData.push(data.exportResult.contractorDollarCard.fileName);
    if (data.exportResult.hourlyDollarCard.data.length === 0) noData.push(data.exportResult.hourlyDollarCard.fileName);
    if (data.exportResult.salariedDollarCard.data.length === 0) noData.push(data.exportResult.salariedDollarCard.fileName);
    if (data.exportResult.timeCard.data.length === 0) noData.push(data.exportResult.timeCard.fileName);
    if (data.inspectorPaymentCaseList.length === 0) noData.push(data.inspectorPaymentCaseList.fileName);
    if (data.appraiserPaymentCaseList.length === 0) noData.push(data.appraiserPaymentCaseList.fileName);

    setDialog({
      title: "Export Payroll",
      width: "650px",
      children: <>
        <div className="dialog-text">
          Payroll Export Complete, press download to download the files
          {noData.map(s => <div key={s}>The file {s} will not be downloaded because it has no data</div>)}
        </div>
      </>,
      contentActions: <Button onClick={() => onExportDownloadClicked(data)}>Download</Button>,
    });
  };

  const onCloseClicked = force => {
    if (force) {
      setDialog(null);
      closePayPeriod(selectedPayPeriod.value, getPayPeriods, mounted);
    } else {
      setDialog({
        title: "Close Payroll",
        width: "500px",
        children: <>
          <div className="dialog-text">Are you sure you want to close payroll for {selectedPayPeriod.label}?</div>
        </>,
        contentActions: <>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button onClick={() => onCloseClicked(true)}>Continue</Button>
        </>,
      });
    }
  };

  const onSelectPayPeriod = (newValue: LocalPayPeriod) => setSelectedPayPeriod(newValue);

  const isLoading = !payPeriods;

  const isCalculating = lastExecution && !lastExecution.executeFinishDateTime;
  const canExport = lastExecution && lastExecution.canExport;
  const canClose = lastExecution && lastExecution.canClose;
  const exportButtonToolTip = isRunningExport ? "Export is running, it will likely take 5-15 minutes" : "Run payroll and return data to import into the payroll system";
  const calculateButtonToolTip = isCalculating ? "Calculating..." : "Run payroll and view the result";

  let sections;
  if (selectedPayPeriod)
    sections = [
      { title: "Appraisers", columnDefinitions: appraiserPayrollGridDefs, data: payrollSummary?.appraisersPayrollSummary },
      { title: "Field Rep - Employees", columnDefinitions: inspectorPayrollGridDefs, data: payrollSummary?.inspectorsPayrollSummary },
      { title: "Field Rep - Previous Hours", columnDefinitions: inspectorCaseLogGridDefs, data: payrollSummary?.inspectorCaseLogDetail},
      { title: "Field Rep - Contractors", columnDefinitions: inspectorContractorPayrollGridDefs, data: payrollSummary?.inspectorContractorsPayrollSummary },
      { title: "Quality Assurance", columnDefinitions: qaPayrollGridDefs, data: payrollSummary?.qaPayrollSummary },
      { title: "Scheduling Assistants", columnDefinitions: saGridDefs, data: payrollSummary?.schedulingUsersPayPeriodSummary },
    ].map(s => ({
      title: s.title,
      children: <MGrid
        actions={[{ code: ACTION_DOWNLOAD }]}
        headerText="Employee time review"
        downloadFileName={`${s.title} time review ${selectedPayPeriod.label}`}
        columnDefinitions={s.columnDefinitions}
        data={s.data} />
      }));

  return (
    <div className="payroll padding-page-content">
      {dialog && (
        <MDialog
          id="payroll-dialog"
          title={dialog.title}
          actions={[{ code: ACTION_CLOSE, onClick: () => setDialog(null) }]}
          width={dialog.width}
          contentActions={dialog.contentActions}
        >
          {dialog.children}
        </MDialog>)}
      {isLoading ? <MSpinner /> : <>
      <MCard className="payroll-card" title="Run Payroll" sx={{ height: "auto" }}>
        <MSelect
          className="payroll-dropdown"
          value={selectedPayPeriod}
          onChange={onSelectPayPeriod}
          options={payPeriods}
          isClearable={false}
          getMenuPortalTarget={() => document.body}
        />
        {canRunPayroll && <>
          <Button title={calculateButtonToolTip} disabled={isCalculating || !selectedPayPeriod || selectedPayPeriod.isClosed} onClick={onCalculateClicked}>{isCalculating ? "Calculating..." : "Calculate"}</Button>
          <Button title={exportButtonToolTip} disabled={isRunningExport || !selectedPayPeriod || selectedPayPeriod.isClosed || !canExport} onClick={onExportClicked}>{isRunningExport ? "Exporting..." : "Export"}</Button>
          <Button title="Close this pay period" disabled={!canClose || !selectedPayPeriod || selectedPayPeriod.isClosed} onClick={() => onCloseClicked(false)}>Close</Button>
        </>}
        <span>{summaryText}</span>
      </MCard>
      {selectedPayPeriod && (payrollSummary ? <MSectionCollection sections={sections} disableModeChanging tabModeCollapsible={false} defaultMode={MSECTION_MODE_TAB} /> : <MSpinner />)}
      </>}
    </div>);
};

const mapStateToProps = state => ({
  canRunPayroll: userHasRight(PAYROLL_RUN)(state),
});

Payroll.propTypes = {
  canRunPayroll: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(Payroll);
